import React, { useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import { fetchReports } from '../detailApi'
import useOperationExpansions from '../useOperationExpansions'
import MilestonesTimeline, { milestoneEntriesOf } from './MilestonesTimeline'
import ProgressSummaryStrip from './ProgressSummaryStrip'
import OperationsProgressTable from './OperationsProgressTable'
import OrderReportsList from './OrderReportsList'

export { TABLE_MIN_WIDTH, TABLE_AVAILABLE_WIDTH_AT_1280, TABLE_AVAILABLE_WIDTH_AT_1200 } from './OperationsProgressTable'

const PAGE_SIZE = 20

// Several pages of reports can arrive twice (a new report moves the pages): append by reportId.
const appendNewReports = (current, incoming) => {
  const known = new Set(current.map((report) => report.reportId))
  return [...current, ...incoming.filter((report) => !known.has(report.reportId))]
}

const EMPTY_REPORTS = { content: [], totalElements: 0, page: 0, size: PAGE_SIZE }

// CU-L9/CU-L10 — Progress block: milestones, summary (H8: literally the header's quantity
// breakdown, no recalculation), per-operation progress (RN62, same source as the Operations
// dropdown) and the shop-floor report list (RN63/RN63.1, paginated, no time-declared column,
// quantities never grouped/summed/labelled good-or-rejected, RN8.2/H2). Read-only throughout
// (RN11/S189): no button here ever reports, corrects or cancels production.
// It owns the reports (the table rows and the list read the same ones) and puts the four parts together.
const ProgressBlock = ({ fabricationOrderId, progress = {}, workflowOperations = [], workflowStatus = 'ready', onRetryWorkflow = () => {}, statusProgress = '', expansionCache }) => {
  // The cache of what the opened operation loads, shared with Operations and kept by the container.
  // Without it (the block mounted alone) the block keeps its own.
  const ownExpansionCache = useOperationExpansions(fabricationOrderId)
  const cache = expansionCache || ownExpansionCache
  const summary = progress.summary || {}
  const operations = progress.operations || []

  const [reports, setReports] = useState(EMPTY_REPORTS)
  // RN8: the list of the order has its own loading and error states, never an empty list on failure.
  const [reportsStatus, setReportsStatus] = useState('loading')
  const [loadingMoreReports, setLoadingMoreReports] = useState(false)
  const [loadMoreReportsFailed, setLoadMoreReportsFailed] = useState(false)
  const reportsRequest = useRef(0)
  const dropPendingReports = () => { reportsRequest.current++ }
  const loadReports = () => {
    const token = ++reportsRequest.current
    setReportsStatus('loading')
    fetchReports(fabricationOrderId, 0, PAGE_SIZE)
      .then((page) => {
        if (token !== reportsRequest.current) return
        setReports(page)
        setReportsStatus('ready')
      })
      .catch(() => { if (token === reportsRequest.current) setReportsStatus('error') })
  }

  useEffect(() => {
    setReports(EMPTY_REPORTS)
    loadReports()
    // A newer load (or another order) makes the older answers stale.
    return dropPendingReports
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricationOrderId])

  const loadMoreReports = () => {
    const token = reportsRequest.current
    setLoadingMoreReports(true)
    setLoadMoreReportsFailed(false)
    fetchReports(fabricationOrderId, reports.page + 1, PAGE_SIZE)
      .then((page) => {
        if (token !== reportsRequest.current) return
        setReports((prev) => ({ ...page, content: appendNewReports(prev.content, page.content) }))
      })
      .catch(() => { if (token === reportsRequest.current) setLoadMoreReportsFailed(true) })
      .finally(() => { if (token === reportsRequest.current) setLoadingMoreReports(false) })
  }

  // The reports already loaded, grouped once per load by operation: N11 carries
  // `operation {jobReferenceId, name}` in each report (contract §C.2), so the correlation needs no
  // new call. A Map instead of a filter per row: the rows ask for it several times each.
  const reportsByJob = useMemo(() => {
    const groups = new Map()
    reports.content.forEach((report) => {
      const key = report.operation?.jobReferenceId
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(report)
    })
    return groups
  }, [reports.content])
  const reportsOf = (jobReferenceId) => reportsByJob.get(jobReferenceId) || []

  const hasMoreReports = reports.content.length < reports.totalElements
  // ⚠ El ancla STARTED, y no «alguna entrada STARTED»: con S419 una clase puede repetirse, pero
  // el arranque sigue siendo uno. Se toma el primero, que es el que Sun sirve como ancla.
  const noOperationStarted = milestoneEntriesOf(progress.milestones).find((entry) => entry.kind === 'STARTED')?.milestone.state !== 'REACHED'

  return (
    <Box data-testid='progress-block'>
      <MilestonesTimeline milestones={progress.milestones} statusProgress={statusProgress} />

      <ProgressSummaryStrip summary={summary} noOperationStarted={noOperationStarted} />

      <OperationsProgressTable
        operations={operations}
        workflowOperations={workflowOperations}
        workflowStatus={workflowStatus}
        onRetryWorkflow={onRetryWorkflow}
        expansionCache={cache}
        reports={{ reportsOf, reportsStatus, hasMoreReports, loadingMoreReports, loadReports, loadMoreReports }}
      />

      <OrderReportsList
        reports={reports}
        reportsStatus={reportsStatus}
        hasMoreReports={hasMoreReports}
        loadingMoreReports={loadingMoreReports}
        loadMoreReportsFailed={loadMoreReportsFailed}
        loadReports={loadReports}
        loadMoreReports={loadMoreReports}
      />
    </Box>
  )
}

export default ProgressBlock
